import React, { useEffect, useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { EditOutlined } from '@ant-design/icons';
import { useAdminSelector } from 'admin/reducers';
import { selectEditableStorySettings } from 'admin/reducers/card-editor/selectors';
import { selectEditableStory, selectStorycardsDomain } from 'admin/reducers/story-editor/selectors';
import { selectEditableElementId } from 'admin/reducers/card-editor-extra/selectors';
import { selectActiveOrganization } from 'admin/reducers/user/selectors';
import { getOrganizationQueryOptions } from 'admin/queries/organization/get-organization';
import { getBrandKitById } from 'common/brand-kit/projection';
import {
	assetIdToReference,
	copyDesignDefinition,
	createDesignDefinition,
	getDesignAssetReferenceId,
	importDesignAssets,
	resolveDesignAssetReference,
	resolveDesignModeValue,
	type DesignAsset,
	type DesignModeSelection,
	type DesignModeValue,
} from 'common/design-tokens';
import { cutURLMethod, replaceUrlHostname } from 'common/utils/assets';
import { toast } from 'admin/components/common/MessageContainer/toast';
import DesignModeValueField from 'admin/components/common/DesignModeValueField';
import DashboardTooltip from 'admin/components/common/Tooltip';
import { pickerSearchClassName } from 'admin/components/common/PickerSearch';
import PanelTooltip from 'admin/components/pages/Story/PanelTooltip';
import type { DesignBindingControlProps } from './DesignTokenControl';
import { useInspectorDesignModeSelection } from './useInspectorDesignModeSelection';
import css from './DesignTokenControl.scss';
import TokenPill from './TokenPill';
import { usePopoverChildProps } from '../Popover';

type Source = 'story' | 'brandKit';

export default function DesignAssetControl({ literalComponent: Literal, ...props }: DesignBindingControlProps) {
	const settings = useAdminSelector(selectEditableStorySettings);
	const story = useAdminSelector(selectEditableStory);
	const elementId = useAdminSelector(selectEditableElementId);
	const activeOrganization = useAdminSelector(selectActiveOrganization);
	const domain = useAdminSelector(selectStorycardsDomain);
	const selection = useInspectorDesignModeSelection();
	const organizationQuery = useQuery({
		...getOrganizationQueryOptions(story?.organizationId ?? ''),
		enabled: !!story?.organizationId && story.organizationId === activeOrganization?.id,
	});
	const organization =
		organizationQuery.data ?? (activeOrganization?.id === story?.organizationId ? activeOrganization : undefined);
	const library = getBrandKitById(organization?.metadata?.brandKits, settings.brandKitId)?.design;
	const design = settings?.design ?? createDesignDefinition();
	const raw = String(props.input.value || props.forcedValue || '');
	const assetId = getDesignAssetReferenceId(raw);
	const asset = design.assets.assets.find(item => item.id === assetId);
	const resolved = resolveDesignAssetReference(raw, design, selection) ?? '';
	const [open, setOpen] = useState(false);
	const popoverChildProps = usePopoverChildProps();
	const [search, setSearch] = useState('');
	const [draft, setDraft] = useState<DesignModeValue<string>>();
	const [busy, setBusy] = useState(false);
	const latest = useRef({ settings, fieldKey: '', raw });
	const fieldKey = `${story?.id}:${elementId}:${props.input.name}`;
	latest.current = { settings, fieldKey, raw };
	const editContext = useRef<{
		settings: typeof settings;
		selection: DesignModeSelection;
		assetId: string;
		raw: string;
		fieldKey: string;
	}>();
	useEffect(
		() => () => {
			latest.current.fieldKey = '';
		},
		[]
	);
	const close = () => {
		setOpen(false);
		setSearch('');
		setDraft(undefined);
		editContext.current = undefined;
	};
	useEffect(close, [fieldKey]);

	const openPicker = () => {
		setDraft(undefined);
		editContext.current = undefined;
		setOpen(true);
	};
	const openEditor = (item: DesignAsset) => {
		editContext.current = { settings, selection, assetId: item.id, raw, fieldKey };
		setDraft(item.value);
		setOpen(true);
	};
	const apply = async (value: string, source: Source = 'story') => {
		setBusy(true);
		try {
			const id = getDesignAssetReferenceId(value);
			const nextDesign =
				source === 'brandKit' && library && id ? importDesignAssets(design, library, [id]) : design;
			if (latest.current.fieldKey !== fieldKey || latest.current.settings !== settings) return;
			if (!props.eventListeners?.onTokenChange) throw new Error('Asset binding is unavailable for this field.');
			await props.eventListeners.onTokenChange({
				name: props.input.name,
				value,
				settings: { ...settings, ...(nextDesign !== design ? { design: nextDesign } : {}) },
			});
			if (latest.current.fieldKey !== fieldKey) return;
			props.input.onChange(value);
			close();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : String(error));
		} finally {
			setBusy(false);
		}
	};
	const save = async () => {
		const context = editContext.current;
		if (!context || !draft) return;
		setBusy(true);
		try {
			if (
				latest.current.fieldKey !== context.fieldKey ||
				latest.current.settings !== context.settings ||
				latest.current.raw !== context.raw
			)
				throw new Error('Story or selection changed. Reopen the asset editor.');
			const nextDesign = copyDesignDefinition(design);
			const nextAsset = nextDesign.assets.assets.find(item => item.id === context.assetId);
			if (!nextAsset) throw new Error('Asset is unavailable.');
			nextAsset.value = draft;
			if (!props.eventListeners?.onTokenValueChange)
				throw new Error('Asset editing is unavailable for this field.');
			await props.eventListeners.onTokenValueChange({
				...settings,
				design: copyDesignDefinition(nextDesign),
			});
			close();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : String(error));
		} finally {
			setBusy(false);
		}
	};
	const query = search.trim().toLowerCase();
	const filter = (item: DesignAsset) =>
		!query || [item.name, item.role, item.id].some(value => value.toLowerCase().includes(query));
	const storyAssets = design.assets.assets.filter(filter);
	const storyAssetIds = new Set(design.assets.assets.map(item => item.id));
	const matchingLibraryAssets = (library?.assets.assets ?? []).filter(filter);
	// Brand Kit assets the Story already has are picked from the Story section; the heading counts them
	const libraryAssets = matchingLibraryAssets.filter(item => !storyAssetIds.has(item.id));
	const libraryAssetsInStory = matchingLibraryAssets.length - libraryAssets.length;
	const editingModes = design.modeGroups
		.map(group => group.modes.find(mode => mode.id === editContext.current?.selection[group.id])?.name)
		.filter(Boolean)
		.join(' / ');
	const content =
		draft && editContext.current ? (
			<div className={css.assetPanel} onKeyDown={event => event.key === 'Escape' && close()}>
				<header className={css.editorHeader}>
					<span>Edit asset value · {editingModes}</span>
					<strong>{asset?.name}</strong>
				</header>
				<div className={css.editorBody}>
					<DesignModeValueField
						value={draft}
						groups={design.modeGroups}
						selection={editContext.current.selection}
						disabled={busy}
						onChange={setDraft}
					>
						{({ value, setValue }) => (
							<Input
								aria-label="Asset image URL"
								value={value}
								disabled={busy}
								onChange={event => setValue(event.target.value)}
							/>
						)}
					</DesignModeValueField>
					<p className={css.editorHelp}>Updates every use of this asset in this Story.</p>
				</div>
				<footer className={css.editorFooter}>
					<Button disabled={busy} onClick={close}>
						Cancel
					</Button>
					<Button type="primary" loading={busy} onClick={save}>
						Save
					</Button>
				</footer>
			</div>
		) : (
			<div className={css.assetPanel} onKeyDown={event => event.key === 'Escape' && close()}>
				<header className={css.pickerHeader}>
					<strong>Connect asset</strong>
					<Input
						autoFocus
						allowClear
						className={pickerSearchClassName}
						aria-label="Search assets"
						placeholder="Search assets"
						value={search}
						onChange={event => setSearch(event.target.value)}
					/>
				</header>
				<div className={css.tokenList}>
					{(
						[
							{ source: 'story', name: 'Story', assets: storyAssets, hiddenCount: 0 },
							{
								source: 'brandKit',
								name: 'Brand Kit',
								assets: libraryAssets,
								hiddenCount: libraryAssetsInStory,
							},
						] as const
					).map(section =>
						section.assets.length ? (
							<section className={css.source} key={section.source}>
								<h3>
									{section.name}
									{section.hiddenCount > 0 ? (
										<span>{section.hiddenCount} already in Story</span>
									) : null}
								</h3>
								{section.assets.map(item => {
									const selected = section.source === 'story' && item.id === assetId;
									return (
										<div className={css.assetItem} key={item.id}>
											<button
												type="button"
												className={css.assetRow}
												data-selected={selected}
												aria-pressed={selected}
												disabled={props.disabled || busy}
												onClick={() => apply(assetIdToReference(item.id), section.source)}
											>
												<img
													className={css.assetThumbnail}
													src={replaceUrlHostname(
														cutURLMethod(resolveDesignModeValue(item.value, selection)),
														domain
													)}
													alt=""
												/>
												<span className={css.rowName}>{item.name}</span>
												<span className={css.rowValue}>{item.role}</span>
											</button>
											{section.source === 'story' ? (
												<button
													type="button"
													className={css.rowEdit}
													data-selected={selected}
													aria-label={`Edit asset value: ${item.name}`}
													title="Edit asset value"
													disabled={props.disabled || busy}
													onClick={() => openEditor(item)}
												>
													<EditOutlined />
												</button>
											) : null}
										</div>
									);
								})}
							</section>
						) : null
					)}
					{!storyAssets.length && !libraryAssets.length ? (
						<div className={css.empty}>No matching assets.</div>
					) : null}
				</div>
				{assetId ? (
					<footer className={css.pickerFooter}>
						<Button
							type="text"
							disabled={props.disabled || busy}
							onClick={() => apply(asset ? resolved : '')}
						>
							{asset ? 'Use image URL' : 'Remove reference'}
						</Button>
					</footer>
				) : null}
			</div>
		);

	const unlinkLabel = asset ? 'Use image URL' : 'Remove reference';
	return (
		<div className={css.control}>
			<Literal {...props} previewValue={assetId ? resolved : undefined} onReferenceChange={apply} />
			<div className={css.assetBinding}>
				<DashboardTooltip
					stylePreset="dashboard"
					destroyOnHidden
					trigger="click"
					open={open}
					onOpenChange={next => (next ? setOpen(true) : close())}
					content={<div {...popoverChildProps}>{content}</div>}
					placement="left"
					autoAdjustOverflow
				>
					<span className={css.binding}>
						{assetId ? (
							<TokenPill
								className={css.pill}
								label={`Change asset: ${asset?.name ?? raw}`}
								value={asset ? asset.name : 'Unresolved asset'}
								tooltip={<span className={css.tooltipLabel}>{asset ? asset.name : raw}</span>}
								unresolved={!asset}
								open={open}
								disabled={props.disabled || busy}
								onClick={openPicker}
								onUnlink={() => apply(asset ? resolved : '')}
								unlinkLabel={unlinkLabel}
							/>
						) : (
							<PanelTooltip content="Connect asset" open={open ? false : undefined}>
								<button
									type="button"
									className={css.tokenButton}
									data-open={open}
									aria-label="Connect asset"
									aria-expanded={open}
									disabled={props.disabled || busy}
									onClick={openPicker}
								/>
							</PanelTooltip>
						)}
					</span>
				</DashboardTooltip>
			</div>
		</div>
	);
}
